import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, ref } from "vue";



import type { IAddArticleToCartCmd, IArticleWithOptions } from "@/data/commands/customer/AddArticleToCartCmd";
import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ICartItem, ICustomerCart, IDeliveryAddress } from "@/data/model/Customer";
import type { IService } from "@/data/model/Service";
import { exceptionTypes } from "@/data/util/Config";



import { translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { TaskQueue } from "@/shared/utils/taskQueue";



import { Popup } from "@/components/dialog/Popup";



import { useTableAllocationStore } from "./tableAllocation";


export const useCartStore = defineStore("cart", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const allocation = useTableAllocationStore();

    // define state
    const currentCart = ref<ICustomerCart | null>(null);
    const isUpdating = ref<boolean>(false);
    // selected cart items - kept outside of the items themselves, because every
    // cart change replaces them with a fresh server response
    const selectedCartItemIds = ref<string[]>([]);

    // define getters
    const isDeliveryActive = computed<boolean>(() => {
        return orderService.value?.useCase?.isDeliveryActive === true;
    });
    const totalAmount = computed(() => currentCart.value?.total ?? 0);
    const feesAmount = computed(() => currentCart.value?.fees ?? 0);
    const hasDiscounts = computed(() => currentCart.value?.hasDiscount ?? false);
    const totalAfterDiscountsAndVouchers = computed(() => currentCart.value?.totalPayableAmount ?? 0);
    const totalDiscountsAndVouchersAmount = computed(() => currentCart.value?.totalDiscountedAmount ?? 0);
    const isFullyCoveredByDiscounts = computed(
        () =>
            currentCart.value != null &&
            totalDiscountsAndVouchersAmount.value > 0 &&
            totalAmount.value == totalDiscountsAndVouchersAmount.value
    );
    const totalCartMainItems = computed(() => {
        let articles = 0;
        for (const item of items.value) {
            articles += item.quantity;
        }

        return articles;
    });
    const locationId = computed(() => allocation.locationId);
    const items = computed(() => currentCart.value?.items ?? []);
    const hasItems = computed(() => items.value.length > 0);
    // ids of items still present in the cart, so ids left over from removed items are ignored
    const selectedMainCartItemIds = computed(() =>
        items.value.filter((item) => selectedCartItemIds.value.includes(item.id)).map((item) => item.id)
    );
    const isAnyCartItemSelected = computed(() => selectedMainCartItemIds.value.length > 0);
    const hasVouchers = computed(() => currentCart.value?.hasVoucherDiscount ?? false);
    const orderServiceId = computed(() => currentCart.value?.serviceId ?? null);
    const orderService = computed(() => {
        const orderServices = allocation.orderServices.filter((x) => x.id == orderServiceId.value);
        if (orderServices.length > 1) {
            throw "Unexpected number of services.";
        }
        return allocation.hasOrderServices ? orderServices[0] : null;
    });
    const areItemCommentsAllowed = computed(() => orderService.value?.useCase?.areItemCommentsAllowed ?? false);

    const cartRequestsQueue = new TaskQueue();

    // define actions
    async function addArticleToCart(
        articleId: string,
        menuCategoryId: string,
        quantity: number,
        options: IArticleWithOptions[],
        orderService: IService,
        offerId: string,
        openPrice?: number
    ) {
        await cartRequestsQueue.enqueue(async () => {
            try {
                const data: IAddArticleToCartCmd = {
                    article: {
                        articleId: articleId,
                        menuCategoryId: menuCategoryId,
                        quantity: quantity,
                        options: options,
                        openPrice: openPrice,
                    },
                    orderServiceId: orderService.id,
                    offerId: offerId,
                };

                if (hasVouchers.value) {
                    await showConfirmVouchersRemovalPopup(() => processAddArticleToCart(data));
                    console.log("after showConfirmDiscountsRemovalPopup");
                } else {
                    await processAddArticleToCart(data);
                }
            } catch (ex) {
                logError(ex);
                await handleError(ex, translations.cart.failedToAdd);
            }
        });
    }

    async function processAddArticleToCart(data: IAddArticleToCartCmd) {
        isUpdating.value = true;

        try {
            const newCart = await customerDao.addArticleToCart(data);
            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToAdd);
        } finally {
            isUpdating.value = false;
        }
    }

    async function removeAllCartDiscounts() {
        try {
            const updatedCart = await customerDao.removeAllCartDiscounts();
            setCurrentCart(updatedCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToRemove);
        } finally {
            isUpdating.value = false;
        }
    }

    async function removeItemFormCartAsync(item: ICartItem) {
        // remove item from cart, then run API call
        currentCart.value?.items.splice(currentCart.value?.items.indexOf(item), 1);

        isUpdating.value = true;

        try {
            const newCart = await customerDao.removeItemFromCart(item.id, {
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToRemove);
        } finally {
            isUpdating.value = false;
        }
    }

    async function changeCartItemQuantityAsync(item: ICartItem, qty: number) {
        // update first, then run API call
        item.quantity = qty;

        isUpdating.value = true;

        try {
            const newCart = await customerDao.setItemQuantityInCart(item.id, {
                quantity: qty,
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToChange);
        } finally {
            isUpdating.value = false;
        }
    }

    async function decreaseCartItemQuantity(item: ICartItem) {
        const newQuantity = item.quantity - 1;
        if (hasVouchers.value) {
            await showConfirmVouchersRemovalPopup(() => changeCartItemQuantityAsync(item, newQuantity));
        } else {
            await changeCartItemQuantityAsync(item, newQuantity);
        }
    }

    async function increaseCartItemQuantity(item: ICartItem) {
        const newQuantity = item.quantity + 1;
        if (hasVouchers.value) {
            await showConfirmVouchersRemovalPopup(() => changeCartItemQuantityAsync(item, newQuantity));
        } else {
            await changeCartItemQuantityAsync(item, newQuantity);
        }
    }

    async function removeCartItem(item: ICartItem) {
        if (hasVouchers.value) {
            await showConfirmVouchersRemovalPopup(() => removeItemFormCartAsync(item));
        } else {
            await removeItemFormCartAsync(item);
        }
    }

    function isCartItemSelected(item: ICartItem) {
        return selectedCartItemIds.value.includes(item.id);
    }

    function toggleCartItemSelection(item: ICartItem) {
        if (isCartItemSelected(item)) {
            selectedCartItemIds.value = selectedCartItemIds.value.filter((id) => id != item.id);
        } else {
            selectedCartItemIds.value = [...selectedCartItemIds.value, item.id];
        }
    }

    function selectAllCartItems() {
        selectedCartItemIds.value = items.value.map((item) => item.id);
    }

    function deselectAllCartItems() {
        selectedCartItemIds.value = [];
    }

    async function setCartItemComment(item: ICartItem, comment: string) {
        // update first, then run API call
        item.comment = comment;

        isUpdating.value = true;

        try {
            const newCart = await customerDao.setCartItemComment(item.id, {
                comment: comment,
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToChange);
        } finally {
            isUpdating.value = false;
        }
    }

    async function removeCartItemComment(item: ICartItem) {
        // update first, then run API call
        item.comment = undefined;

        isUpdating.value = true;

        try {
            const newCart = await customerDao.removeCartItemComment(item.id, {
                orderServiceId: orderServiceId.value!,
            });

            setCurrentCart(newCart);
        } catch (ex) {
            logError(ex);
            handleError(ex, translations.cart.failedToChange);
        } finally {
            isUpdating.value = false;
        }
    }

    function getCartItemQuantity(articleId: string) {
        const cartItems = getCartItems(articleId);

        let totalQuantity = 0;
        for (const cartItem of cartItems) {
            totalQuantity += cartItem.quantity;
        }
        return totalQuantity;
    }

    // functions rather than computed on purpose: when the order application changes, the app's AppContext
    // replaces `useCase` on the raw service object, which Vue does not track, so a computed would keep the old limit
    function isMinimumTotalUnitsPerOrderReached() {
        const minimumTotalUnitsPerOrder = orderService.value?.useCase?.minimumTotalUnitsPerOrder;
        return minimumTotalUnitsPerOrder == null || totalCartMainItems.value >= minimumTotalUnitsPerOrder;
    }

    function getMissingUnitsToReachMinimumTotalUnitsPerOrder() {
        const minimumTotalUnitsPerOrder = orderService.value?.useCase?.minimumTotalUnitsPerOrder;
        return minimumTotalUnitsPerOrder == null ? 0 : Math.max(minimumTotalUnitsPerOrder - totalCartMainItems.value, 0);
    }

    function isMaximumTotalUnitsPerOrderExceeded() {
        const maximumTotalUnitsPerOrder = orderService.value?.useCase?.maximumTotalUnitsPerOrder;
        return maximumTotalUnitsPerOrder != null && totalCartMainItems.value > maximumTotalUnitsPerOrder;
    }

    function getCartItems(articleId: string) {
        const foundCartItems: ICartItem[] = [];
        for (const item of items.value) {
            if (item.articleId == articleId) {
                foundCartItems.push(item);
            }
        }

        return foundCartItems;
    }

    async function loadCurrentCart() {
        try {
            isUpdating.value = true;
            const newCart = await customerDao.getCart();
            currentCart.value = newCart;
        } finally {
            isUpdating.value = false;
        }
    }

    function setCurrentCart(cart: ICustomerCart | null) {
        currentCart.value = cart;
    }

    function resetCart() {
        setCurrentCart(null);
    }

    async function deleteCartsFromLocation() {
        isUpdating.value = true;
        try {
            await customerDao.removeCartsFromLocation();
            currentCart.value = null;
        } finally {
            isUpdating.value = false;
        }
    }

    async function selectFulfillmentSlot(orderServiceId: string, selectedDate: Date, selectedTime: string) {
        isUpdating.value = true;
        try {
            await customerDao.selectCartFulfillmentSlot({
                orderServiceId: orderServiceId,
                selectedDate: selectedDate,
                selectedTime: selectedTime,
                socketConnectionId: undefined,
            });

            currentCart.value = null;
        } finally {
            isUpdating.value = false;
        }
    }

    function handleError(error: any, defaultMsg: string) {
        if (error instanceof DaoError) {
            switch (error.type) {
                case exceptionTypes.paymentInProgressException:
                    Popup.show({
                        message: translations.cart.paymentBlockingCart,
                    });
                    break;
                case exceptionTypes.openPriceIsMissingForArticleToCartException:
                case exceptionTypes.articleCannotBeAddedToCartException:
                    Popup.show({
                        message: translations.cart.failedToAdd,
                    });
                    break;

                default:
                    if (isVouchersAppliedToCartException(error)) {
                        showConfirmVouchersRemovalPopup();
                    }
                    break;
            }
        } else {
            Popup.show({ message: defaultMsg });
        }
    }

    function isVouchersAppliedToCartException(error: any) {
        return error instanceof DaoError && error.type === exceptionTypes.vouchersAppliedToCartException;
    }

    function showConfirmVouchersRemovalPopup(onRemoveAllCartVouchersConfirmed?: () => Promise<void>) {
        return new Promise<void>((resolve) => {
            const isShown = Popup.show({
                message: translations.cart.changingTotalWillRemoveAppliedVouchersMessage,
                confirmHandler: async () => {
                    try {
                        const newCart = await customerDao.removeAllCartVouchers();
                        setCurrentCart(newCart);

                        if (onRemoveAllCartVouchersConfirmed) {
                            await onRemoveAllCartVouchersConfirmed();
                        }
                        resolve();
                    } catch (error) {
                        logError(error);
                        handleError(error, translations.general.operationFailed);
                        resolve();
                    }
                },
                cancelHandler: () => {
                    loadCurrentCart();
                    resolve();
                },
                confirmButton: translations.cart.removeVouchers,
                cancelButton: translations.general.cancel,
                highlightConfirm: false,
                highlightCancel: true,
                confirmBtnOnTheLeft: false,
                uniqueId: "changingTotalWillRemoveAppliedVouchersPopup",
            });

            if (!isShown) {
                resolve();
            }
        });
    }

    function showConfirmItemDiscountsRemovalPopup(item: ICartItem) {
        Popup.show({
            message: translations.discounts.removeAllItemDiscountsPrompt,
            confirmHandler: async () => {
                try {
                    const newCart = await customerDao.removeCartItemDiscounts(item.id, {
                        orderServiceId: orderServiceId.value!,
                    });
                    setCurrentCart(newCart);
                } catch (error) {
                    logError(error);
                    handleError(error, translations.general.operationFailed);
                }
            },
            confirmButton: translations.general.remove,
            cancelButton: translations.general.cancel,
            highlightConfirm: false,
            highlightCancel: true,
            confirmBtnOnTheLeft: false,
            uniqueId: "confirmItemDiscountsRemovalPopup",
        });
    }

    function setContactData(
        firstName: string | undefined,
        lastName: string | undefined,
        email: string | undefined,
        phoneNumber: string | undefined,
        indirectIdentityId: string
    ) {
        currentCart.value!.contactDataEndUser = {
            identityId: indirectIdentityId,
            firstName: firstName,
            lastName: lastName,
            email: email,
            phoneNumber: phoneNumber,
        };
    }

    function setDeliveryAddress(
        deliveryAddress: IDeliveryAddress | undefined,
    ) {
        currentCart.value!.deliveryAddress = deliveryAddress;
    }

    return {
        // state
        currentCart,
        isUpdating,
        selectedCartItemIds,

        // getters
        locationId,
        totalAmount,
        feesAmount,
        totalAfterDiscountsAndVouchers,
        totalDiscountsAndVouchersAmount,
        totalCartMainItems,
        items,
        hasItems,
        selectedMainCartItemIds,
        isAnyCartItemSelected,
        hasVouchers,
        orderService,
        areItemCommentsAllowed,
        isFullyCoveredByDiscounts,
        hasDiscounts,
        isDeliveryActive,

        // actions
        decreaseCartItemQuantity,
        increaseCartItemQuantity,
        removeCartItem,
        isCartItemSelected,
        toggleCartItemSelection,
        selectAllCartItems,
        deselectAllCartItems,
        getCartItemQuantity,
        isMinimumTotalUnitsPerOrderReached,
        getMissingUnitsToReachMinimumTotalUnitsPerOrder,
        isMaximumTotalUnitsPerOrderExceeded,
        addArticleToCart,
        loadCurrentCart,
        setCurrentCart,
        resetCart, // todo: in pay context
        deleteCartsFromLocation,
        selectFulfillmentSlot,
        removeAllCartDiscounts,
        setCartItemComment,
        removeCartItemComment,
        showConfirmItemDiscountsRemovalPopup,
        setContactData,
        setDeliveryAddress
    };
});

export type CartStore = ReturnType<typeof useCartStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot));
}
